import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { getMessages, type Messages } from './messages'
import { resolveAppLocale } from './resolveLocale'
import { setRuntimeLocale } from './runtime'
import { readLocalePreference, writeLocalePreference } from './storage'
import type { AppLocale, LocalePreference } from './types'

export type LocaleContextValue = {
  preference: LocalePreference
  locale: AppLocale
  setPreference: (next: LocalePreference) => void
  t: Messages
}

const LocaleContext = createContext<LocaleContextValue | null>(null)

function syncDocumentLocale(locale: AppLocale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale
  }
}

function useLocaleState(): LocaleContextValue {
  const [preference, setPreferenceState] = useState<LocalePreference>(() => readLocalePreference())

  const locale = useMemo(() => {
    const resolved = resolveAppLocale(preference)
    // Sincronizar en el mismo tick que React calcula `locale` para que los proxies
    // (pageHeaderCopy, nutrimaticLabels) lean el idioma correcto durante el render.
    setRuntimeLocale(resolved)
    syncDocumentLocale(resolved)
    return resolved
  }, [preference])

  const t = useMemo(() => getMessages(locale), [locale])

  const setPreference = useCallback((next: LocalePreference) => {
    setPreferenceState(prev => {
      if (prev === next) return prev
      writeLocalePreference(next)
      return next
    })
  }, [])

  const value = useMemo(
    () => ({ preference, locale, setPreference, t }),
    [preference, locale, setPreference, t],
  )

  return value
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const value = useLocaleState()
  return React.createElement(LocaleContext.Provider, { value }, children)
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext)
  if (!ctx) {
    throw new Error('useLocale must be used within LocaleProvider')
  }
  return ctx
}

/** Alias para acceder a los mensajes traducidos. */
export function useTranslation(): Messages {
  return useLocale().t
}
